// ─── src/api/investorLounge.ts ───────────────────────────────────────────────
//
// Admin API for the two GEW Investor Lounge inboxes: founder applications
// and investor applications. Both endpoints are guarded by x-api-key on the
// backend; the public site only ever POSTs.

import { apiClient } from "./registration";
import type { PaginationMeta } from "../types/registration";

// ── Founder side ─────────────────────────────────────────────────────────────

export interface InvestorLoungeFounder {
  id: string;
  founderName: string;
  founderRole: string;
  founderEmail: string;
  founderPhone: string;
  founderLinkedin?: string | null;

  startupName: string;
  startupOneLiner: string;
  startupIndustry: string;
  startupStage: string;
  startupHq: string;
  startupWebsite?: string | null;

  tractionRevenue?: string | null;
  tractionUsers?: string | null;
  tractionGrowth?: string | null;
  fundingRaised?: string | null;

  meetingGoal: string;
  meetingUnlock: string;
  preferredInvestorProfile?: string | null;

  declarationAccuracy: string;
  declarationConfidentiality: string;

  createdAt: string;
  updatedAt: string;
}

export interface InvestorLoungeFounderParams {
  page?: number;
  limit?: number;
  search?: string;
  stage?: string;
  industry?: string;
}

export interface InvestorLoungeFounderStats {
  total: number;
  byStage: Record<string, number>;
  byIndustry: Record<string, number>;
}

// ── Investor side ────────────────────────────────────────────────────────────

export interface InvestorLoungeInvestor {
  id: string;
  investorName: string;
  investorRole: string;
  investorEmail: string;
  investorPhone?: string | null;
  investorLinkedin?: string | null;

  firmName: string;
  firmType: string;
  aumRange?: string | null;
  stageFocus: string;
  geoFocus: string;
  thesis: string;
  firmWebsite?: string | null;

  availability: string;
  meetingCapacity: string;

  declarationConfidentiality: string;
  declarationContact: string;

  createdAt: string;
  updatedAt: string;
}

export interface InvestorLoungeInvestorParams {
  page?: number;
  limit?: number;
  search?: string;
  firmType?: string;
}

export interface InvestorLoungeInvestorStats {
  total: number;
  byFirmType: Record<string, number>;
}

// ── Shared list shape ────────────────────────────────────────────────────────

interface Paged<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface NormalisedList<T> {
  data: T[];
  meta: PaginationMeta;
}

const normalise = <T,>(raw: Paged<T>): NormalisedList<T> => ({
  data: raw.data,
  meta: {
    total: raw.total,
    page: raw.page,
    limit: raw.limit,
    totalPages: Math.ceil(raw.total / raw.limit),
  },
});

const clean = <T extends object>(params: T) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== "" && v !== null,
    ),
  );

// ── Calls ────────────────────────────────────────────────────────────────────

export const getInvestorLoungeFounders = async (
  params: InvestorLoungeFounderParams,
): Promise<NormalisedList<InvestorLoungeFounder>> => {
  const { data } = await apiClient.get<Paged<InvestorLoungeFounder>>(
    "/investor-lounge/founders",
    { params: clean(params) },
  );
  return normalise(data);
};

export const getInvestorLoungeFounderStats = async (): Promise<InvestorLoungeFounderStats> => {
  const { data } = await apiClient.get<InvestorLoungeFounderStats>(
    "/investor-lounge/founders/stats",
  );
  return data;
};

export const deleteInvestorLoungeFounder = async (id: string): Promise<void> => {
  await apiClient.delete(`/investor-lounge/founders/${id}`);
};

export const getInvestorLoungeInvestors = async (
  params: InvestorLoungeInvestorParams,
): Promise<NormalisedList<InvestorLoungeInvestor>> => {
  const { data } = await apiClient.get<Paged<InvestorLoungeInvestor>>(
    "/investor-lounge/investors",
    { params: clean(params) },
  );
  return normalise(data);
};

export const getInvestorLoungeInvestorStats = async (): Promise<InvestorLoungeInvestorStats> => {
  const { data } = await apiClient.get<InvestorLoungeInvestorStats>(
    "/investor-lounge/investors/stats",
  );
  return data;
};

export const deleteInvestorLoungeInvestor = async (id: string): Promise<void> => {
  await apiClient.delete(`/investor-lounge/investors/${id}`);
};
